/*
* Created on Jan 22, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import React from 'react';
import {useOutletContext, useParams} from "react-router-dom";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import TableComponent from "../common/TableComponent/TableComponent";
import ic_content_group_svg from "../../static/img/content/ic-content-group.svg";
import GroupTableRowComponent from "./GroupTableRowComponent";
import {useState, useReducer, useEffect} from "react";
import GroupEditModalComponent from "./GroupEditModalComponent";
import ModalAlertComponent from "../common/ModalComonent/ModalAlertComponent";
import GroupCreateModalComponent from "./GroupCreateModalComponent";
import {useAuthSession} from "../../context/AuthSessionContext";
import {useTranslation} from "react-i18next";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useOrganization} from "../../context/OrganizationContext";
import useEmailChangedHook from "../../hook/EmailChangedHook";

const table_ui = {
    toolbar: {
        count: true,
        create_button: true,
        delete_button: true,
        export_button: false
    },
    table_field_checkbox: true
}


const GroupIndexComponent = () => {
    const { userPermissions, refetchPermissions } = useOutletContext();
    const hasPermission = userPermissions?.role === 'user_manager' || userPermissions?.hasPermission('manage_group');
    const {t, i18n} = useTranslation();
    const {userProfile, getCurrentOrg, setCurrentOrg} = useOrganization();
    const {EmailWarningDialog} = useEmailChangedHook();
    const {is_owner} = userProfile();
    const {OrganizationAPI} = useOrganizationApi();
    const queryClient = useQueryClient();
    const {useCheckSession} = useAuthSession();
    const {oid} = useParams();
    const [page, setPage] = useState({limit:10, offset:0})
    const [refresh, setRefresh] = useState(1);
    const { data, isSuccess } = useQuery({
        queryKey: [`organization/${oid}/group`, page, refresh, i18n.language],
        queryFn: async ({signal}) => {
            const group_list = await OrganizationAPI.get(`organization/${oid}/group`, {...page, gt: true, lang: i18n.language}, false, signal);
            return group_list.result;
        }
    });

    const table_head_fields = [
        {field_name: t('@Group Name@'), isSortable: true, width_style: '!w-20', field_id: 'name', default_sort: true, default_sort_order: ''},
        {field_name: t('@Description@'), isSortable: false, width_style: '!w-25'},
        {field_name: t('@Members@'), isSortable: false, width_style: '!w-25'},
        {field_name: t('@Creation date@'), isSortable: true, width_style: '!w-20', field_id: 'created_at', default_sort: false, default_sort_order: '-'},
        {field_name: t('@Action@'), isSortable: false, width_style: '!w-10'},
    ];

    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [alertConfirmAction, setAlertConfirmAction] = useState({});
    const [rowData, setRowData] = useState(null);
    const closeModal = (()=>{
        setOpenModal(false);
    });

     const closeCreateModal = (()=>{
        setOpenCreateModal(false);
    });

    const dispatch_handler = (group_state, action) => {
        switch (action.type) {
            case 'init':
                group_state.data = action.payload.groups;
                group_state.total_record= action.payload.total;
                group_state.select_box = false;
                group_state.userPermissions = userPermissions;
                group_state.search = (obj, query='') => {
                    return obj.name.match(new RegExp(query, 'i')) ||
                        obj.users.filter((u)=>{return u.email.match(new RegExp(query, 'i'))}).length > 0
                }
                return {...group_state};
            case 'pagination':
                setPage({limit:action.payload.limit, offset:action.payload.offset});
                if (action.payload.refresh) {
                    setRefresh(prev=>prev+1);
                }
                return {...group_state}
            case 'select_all':
                return {
                    ...group_state,
                    data: group_state.data.map((g)=>({...g, checked: true})),
                    select_box: true
                }
            case 'select_none':
                return {
                    ...group_state,
                    data: group_state.data.map((g)=>({...g, checked: false})),
                    select_box: false
                }
            case 'toggle_checkbox':
                const d = group_state.data.map((o) => {
                    if (action.payload.organization_group_id === o.organization_group_id) {
                        return {...o, checked: !o.checked}
                    }
                    return o;
                });
                let check_status = d?.every((o) => o.checked);
                if (!check_status) {
                    check_status = d?.some((o) => o.checked) ? 'partial' : false;
                }
                return {
                    ...group_state,
                    data: d,
                    select_box: check_status
                }
            case 'delete_group':
                const doDeleteGroup = async () => {
                    setOpenAlert(false);
                    const group_to_delete = group_state.data.filter((o)=>o.checked).map((o)=>o.organization_group_id);
                    await OrganizationAPI.delete(`organization/${oid}/group`, null, {groups: group_to_delete})
                    const org = getCurrentOrg()
                    org.groups = org.groups.filter((g)=>!group_to_delete.includes(g.organization_group_id));
                    setCurrentOrg(org);
                    queryClient.invalidateQueries({queryKey: [`organization/${oid}/group`], refetchType: 'all'});
                    setRefresh((prevVal)=>prevVal+1);
                    return {
                        ...group_state,
                        data: group_state.data.filter((o,i)=>i!==0)
                    }
                }
                doDeleteGroup();
            case 'open_edit_modal':
                setRowData(action.payload);
                setOpenModal(true);
                return group_state;
            case 'open_alert_delete_groups':
                setOpenAlert(true);
                setAlertConfirmAction('delete_group');
                return group_state;
            case 'create_groups':
                setOpenCreateModal(true);
                return group_state;
            case 'search_groups':
                return {
                    ...group_state,
                    query: action.payload
                };
            default:
                return group_state;
        }
    }

    const [state, dispatch] = useReducer(dispatch_handler, {});

    useCheckSession()
    useEffect(()=> {
        if (data) {
            dispatch({type: 'init', payload: data})
        }
    }, [data, refresh])


    return (
        <>
            <div className="content content-page">
                {/*{state &&*/}
                    <TableComponent
                        name='Groups'
                        table_icon={ic_content_group_svg}
                        table_head_fields={table_head_fields}
                        table_data={state}
                        total_record={state.total_record}
                        table_ui={table_ui}
                        row_component={GroupTableRowComponent}
                        doRefresh={setRefresh}
                        disPatcher={dispatch}
                        isOwner={hasPermission}
                        data_table_name="groups-table"
                    >
                    </TableComponent>
                {/*}*/}
            </div>
            {openCreateModal && <GroupCreateModalComponent openModal={openCreateModal} closeModal={closeCreateModal} doRefresh={setRefresh}/>}
            {rowData && <GroupEditModalComponent openModal={openModal} closeModal={closeModal} group_data={rowData} doRefresh={setRefresh}/>}
            {openAlert &&
            <ModalAlertComponent isOpen={openAlert} onClose={setOpenAlert} onConfirmAction={()=>dispatch({type:alertConfirmAction})}>
                <div className="alert-msg-inner-head">{t('@Delete the selected Group(s)?@')}</div>
                <div>
                    <div className="text-wrap">
                        {t('@The members in the removed groups will no longer use any features or access any devices that has been assigned permission.@')}
                    </div>

                    <div className="text-wrap !mb-10">{t('@Selected Groups@')}:</div>
                    <div className="tag-group">
                        {state.data.filter(g=>g.checked).map((g, i)=><span className="tag" key={i}>{g.name}</span>)}
                    </div>
                </div>
            </ModalAlertComponent>}

            {isSuccess && <EmailWarningDialog />}
        </>
    );
};

export default GroupIndexComponent;